import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { useGetTemplates } from '@/generated/http-clients/v3ApiComponents'
import type {
  TemplateDetail,
  TemplatesResponse,
} from '@/generated/http-clients/v3ApiSchemas'
import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type TemplateState = {
  page: number
  data: TemplateDetail[]
  hasNext: boolean
  isLoading: boolean
}

interface TemplatesContextType {
  templatesData?: TemplatesResponse
  templatesIsLoading: boolean
  templatesState: TemplateState
  loadMoreTemplates: () => void
}

const TemplatesContext = createContext<TemplatesContextType | undefined>(
  undefined,
)

export const TemplatesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { hasAdminRights } = useRBACPermissions()

  // State to track the current page and accumulated templates data
  const [templatesState, setTemplatesState] = useState<TemplateState>({
    page: 1,
    data: [],
    hasNext: false,
    isLoading: true,
  })

  // Check if user has admin rights before making API calls
  const canAccessTemplates = hasAdminRights()

  // Use React Query for the first page of templates
  const { data: templatesData, isLoading: templatesIsLoading } =
    useGetTemplates(
      {
        queryParams: { page: 1, order: 'desc' },
      },
      {
        // Skip the query if user doesn't have admin rights
        enabled: canAccessTemplates,
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Cache data between route changes to avoid unnecessary refetches
        gcTime: 10 * 60 * 1000, // 10 minutes cache time
        // Disable automatic refetching to prevent unnecessary API calls
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      },
    )

  // State to track the next page to be fetched
  const [nextPageToFetch, setNextPageToFetch] = useState<number>(2)

  // Use a separate query for additional pages of templates
  const { refetch: fetchAdditionalTemplates } = useGetTemplates(
    {
      queryParams: { page: nextPageToFetch, order: 'desc' },
    },
    {
      // Don't fetch automatically, we'll trigger manually, and only if user has admin rights
      enabled: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Important: This ensures the query reruns with updated params
      refetchOnReconnect: false,
    },
  )

  // Initialize template state with the first page of data
  useEffect(() => {
    if (templatesData?.templates && templatesState.data.length === 0) {
      setTemplatesState({
        page: 1,
        data: templatesData.templates,
        hasNext: templatesData.has_next_page ?? false,
        isLoading: false,
      })
    }
  }, [templatesData, templatesState.data.length])

  // Function to load more templates when the user scrolls down
  const loadMoreTemplates = useCallback(() => {
    // Only allow loading more templates if user has admin rights
    if (
      canAccessTemplates &&
      templatesState.hasNext &&
      !templatesState.isLoading
    ) {
      // Update loading state
      setTemplatesState((state) => ({
        ...state,
        page: state.page + 1,
        isLoading: true,
      }))

      // Fetch the next page
      fetchAdditionalTemplates()
        .then((response) => {
          // After successful fetch, update templates data and increment the next page to fetch
          setTemplatesState((state) => ({
            ...state,
            data: [...state.data, ...(response.data?.templates || [])],
            hasNext: response.data?.has_next_page ?? false,
            isLoading: false,
          }))
          setNextPageToFetch((prev) => prev + 1)
        })
        .catch(() => {
          setTemplatesState((state) => ({
            ...state,
            isLoading: false,
          }))
        })
    }
  }, [
    canAccessTemplates,
    templatesState.hasNext,
    templatesState.isLoading,
    fetchAdditionalTemplates,
  ])

  // Memoize the context value so it only changes when one of its fields changes
  const value = useMemo(
    () => ({
      templatesData,
      templatesIsLoading,
      templatesState,
      loadMoreTemplates,
    }),
    [templatesData, templatesIsLoading, templatesState, loadMoreTemplates],
  )

  return (
    <TemplatesContext.Provider value={value}>
      {children}
    </TemplatesContext.Provider>
  )
}

export const useTemplates = () => {
  const context = useContext(TemplatesContext)
  if (!context) {
    throw new Error('useTemplates must be used within an TemplatesProvider')
  }
  return context
}
